import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Tooltip,
  FormHelperText,
  Divider
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  GetApp as DownloadIcon,
  Print as PrintIcon,
  Share as ShareIcon,
  FilePresent as FileIcon
} from '@mui/icons-material';
import { pacientesIniciais } from '../shared/initialData';

const Documentos = () => {
  const documentosTipos = [
    { id: 'relatorios', label: 'Relatórios' },
    { id: 'declaracoes', label: 'Declarações' },
    { id: 'encaminhamentos', label: 'Encaminhamentos' },
    { id: 'atestados', label: 'Atestados' },
  ];

  const [tabAtiva, setTabAtiva] = useState('relatorios');
  const [documentos, setDocumentos] = useState([
    {
      id: 1,
      titulo: 'Relatório de Acompanhamento Psicológico',
      tipo: 'relatorios',
      pacienteId: 1,
      data: '25/08/2023',
      conteudo: 'Conteúdo detalhado do relatório de acompanhamento psicológico...',
      psicologoId: 1
    },
    {
      id: 2,
      titulo: 'Declaração de Comparecimento',
      tipo: 'declaracoes',
      pacienteId: 2,
      data: '15/08/2023',
      conteudo: 'Declaro para os devidos fins que o paciente compareceu à sessão...',
      psicologoId: 1
    },
    {
      id: 3,
      titulo: 'Encaminhamento para Avaliação Psiquiátrica',
      tipo: 'encaminhamentos',
      pacienteId: 3,
      data: '10/08/2023',
      conteudo: 'Encaminho o paciente para avaliação psiquiátrica devido a...',
      psicologoId: 1
    },
    {
      id: 4,
      titulo: 'Atestado de Afastamento por Questões Emocionais',
      tipo: 'atestados',
      pacienteId: 1,
      data: '20/08/2023',
      conteudo: 'Atesto para os devidos fins que o(a) paciente necessita de afastamento de suas atividades laborais pelo período de 5 dias devido a questões emocionais que requerem acompanhamento psicológico.',
      psicologoId: 1
    }
  ]);
  
  const [openDialog, setOpenDialog] = useState(false);
  const [currentDocumento, setCurrentDocumento] = useState(null);
  const [pacientes, setPacientes] = useState([]);
  const [psicologoId, setPsicologoId] = useState(null);
  const [formErrors, setFormErrors] = useState({});

  const [formData, setFormData] = useState({
    titulo: '',
    tipo: 'relatorios',
    pacienteId: '',
    data: null,
    conteudo: '',
    diasAfastamento: '',
    motivoAfastamento: ''
  });

  // Verificar o psicólogo logado e carregar seus pacientes
  useEffect(() => {
    const tipoUsuario = localStorage.getItem('userType');
    if (tipoUsuario !== 'psicologo') {
      return;
    }

    const storedPsicologoId = localStorage.getItem('userId');
    if (storedPsicologoId) {
      setPsicologoId(parseInt(storedPsicologoId));
      
      // Filtrar pacientes do psicólogo
      const pacientesFiltrados = pacientesIniciais.filter(
        paciente => paciente.psicologoId === parseInt(storedPsicologoId)
      );
      setPacientes(pacientesFiltrados);
    }
  }, []);

  const handleTabChange = (event, newValue) => {
    setTabAtiva(newValue);
  };

  const handleOpenDialog = (documento = null) => {
    if (documento) {
      // Editar documento existente
      const dataArray = documento.data.split('/');
      const data = new Date(dataArray[2], dataArray[1] - 1, dataArray[0]);
      
      setFormData({
        titulo: documento.titulo,
        tipo: documento.tipo,
        pacienteId: documento.pacienteId.toString(),
        data: data,
        conteudo: documento.conteudo,
        diasAfastamento: '',
        motivoAfastamento: ''
      });
      setCurrentDocumento(documento);
    } else {
      // Novo documento
      setFormData({
        titulo: '',
        tipo: tabAtiva,
        pacienteId: '',
        data: new Date(),
        conteudo: '',
        diasAfastamento: '',
        motivoAfastamento: ''
      });
      setCurrentDocumento(null);
    }
    setFormErrors({});
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setFormErrors({});
  };

  const handleInputChange = (field, value) => {
    let novoFormData = {
      ...formData,
      [field]: value
    };

    // Se for um campo específico de atestado, atualizar o conteúdo
    if (field === 'diasAfastamento' || field === 'motivoAfastamento') {
      const { diasAfastamento, motivoAfastamento } = field === 'diasAfastamento' 
        ? { diasAfastamento: value, motivoAfastamento: formData.motivoAfastamento }
        : { diasAfastamento: formData.diasAfastamento, motivoAfastamento: value };

      const pacienteNome = getPacienteNome(formData.pacienteId);
      const conteudoAtualizado = `Atesto para os devidos fins que o(a) paciente ${pacienteNome}, sob meus cuidados profissionais, necessita de afastamento de suas atividades laborais/escolares pelo período de ${diasAfastamento || '____'} dias, a partir da presente data, por motivos de ${motivoAfastamento || 'saúde mental que requerem acompanhamento psicológico'}.

Diagnóstico (CID): ____________

Observações adicionais:
__________________________________________________
__________________________________________________
__________________________________________________

Este atestado tem finalidade de justificar ausência e subsidiar condutas em benefício da saúde do(a) paciente.`;

      novoFormData.conteudo = conteudoAtualizado;
    }

    // Lógica existente para modelo de atestado
    if (field === 'tipo' && value === 'atestados' && (!formData.conteudo || formData.conteudo === '')) {
      // Preencha com um modelo de atestado quando o tipo é alterado para atestados
      novoFormData = {
        ...novoFormData,
        titulo: 'Atestado de Afastamento',
        conteudo: `Atesto para os devidos fins que o(a) paciente ${getPacienteNome(formData.pacienteId)}, sob meus cuidados profissionais, necessita de afastamento de suas atividades laborais/escolares pelo período de ____ dias, a partir da presente data, por motivos de saúde mental que requerem acompanhamento psicológico.

Diagnóstico (CID): ____________

Observações adicionais:
__________________________________________________
__________________________________________________
__________________________________________________

Este atestado tem finalidade de justificar ausência e subsidiar condutas em benefício da saúde do(a) paciente.`
      };
    } else if (field === 'pacienteId' && formData.tipo === 'atestados') {
      // Se mudar o paciente selecionado e o tipo for atestado, atualizar o modelo
      const conteudoAtualizado = `Atesto para os devidos fins que o(a) paciente ${getPacienteNome(value)}, sob meus cuidados profissionais, necessita de afastamento de suas atividades laborais/escolares pelo período de ${formData.diasAfastamento || '____'} dias, a partir da presente data, por motivos de ${formData.motivoAfastamento || 'saúde mental que requerem acompanhamento psicológico'}.

Diagnóstico (CID): ____________

Observações adicionais:
__________________________________________________
__________________________________________________
__________________________________________________

Este atestado tem finalidade de justificar ausência e subsidiar condutas em benefício da saúde do(a) paciente.`;

      novoFormData.conteudo = conteudoAtualizado;
    }

    setFormData(novoFormData);
    
    // Limpar erro quando o usuário corrige o campo
    if (formErrors[field]) {
      setFormErrors({
        ...formErrors,
        [field]: null
      });
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.titulo.trim()) errors.titulo = 'O título é obrigatório';
    if (!formData.pacienteId) errors.pacienteId = 'Selecione um paciente';
    if (!formData.data) errors.data = 'Selecione uma data';
    if (!formData.conteudo.trim()) errors.conteudo = 'O conteúdo é obrigatório';
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSaveDocumento = () => {
    if (!validateForm()) return;
    
    const dataFormatada = formData.data.toLocaleDateString('pt-BR');
    
    if (currentDocumento) {
      // Atualizar documento existente
      const documentosAtualizados = documentos.map(d => {
        if (d.id === currentDocumento.id) {
          return {
            ...d,
            titulo: formData.titulo,
            tipo: formData.tipo,
            pacienteId: parseInt(formData.pacienteId),
            data: dataFormatada,
            conteudo: formData.conteudo
          };
        }
        return d;
      });
      setDocumentos(documentosAtualizados);
    } else {
      // Criar novo documento
      const novoDocumento = {
        id: Math.max(...documentos.map(d => d.id), 0) + 1,
        titulo: formData.titulo,
        tipo: formData.tipo,
        pacienteId: parseInt(formData.pacienteId),
        data: dataFormatada,
        conteudo: formData.conteudo,
        psicologoId: psicologoId
      };
      setDocumentos([...documentos, novoDocumento]);
    }
    
    handleCloseDialog();
  };

  const handleDeleteDocumento = (documentoId) => {
    if (window.confirm('Tem certeza que deseja excluir este documento?')) {
      setDocumentos(documentos.filter(d => d.id !== documentoId));
    }
  };

  const formatarData = (data) => {
    if (!data) return '';
    try {
      const [dia, mes, ano] = data.split('/');
      return new Date(ano, mes - 1, dia).toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: 'long',
        year: 'numeric'
      });
    } catch (error) {
      return data;
    }
  };

  const getPacienteNome = (pacienteId) => {
    const paciente = pacientes.find(p => p.id === pacienteId);
    return paciente ? paciente.nome : 'Paciente não encontrado';
  };

  const documentosFiltrados = documentos.filter(doc => 
    doc.tipo === tabAtiva && doc.psicologoId === psicologoId
  );

  const getTipoDocumentoSingular = (tipo) => {
    switch (tipo) {
      case 'relatorios':
        return 'Relatório';
      case 'declaracoes':
        return 'Declaração';
      case 'encaminhamentos':
        return 'Encaminhamento';
      case 'atestados':
        return 'Atestado';
      default:
        return 'Documento';
    }
  };

  const handleDownload = (documento) => {
    alert(`Download do documento: ${documento.titulo}`);
    // Implementação real de download seria feita aqui
  };

  const handlePrint = (documento) => {
    alert(`Imprimir documento: ${documento.titulo}`);
    // Implementação real de impressão seria feita aqui
  };

  const handleShare = (documento) => {
    alert(`Compartilhar documento: ${documento.titulo}`);
    // Implementação real de compartilhamento seria feita aqui
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Documentos
      </Typography>
      
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Button 
          variant="contained" 
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          sx={{ bgcolor: '#4a8a84', '&:hover': { bgcolor: '#3a7a74' } }}
        >
          Novo {getTipoDocumentoSingular(tabAtiva)}
        </Button>
      </Box>
      
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs 
          value={tabAtiva} 
          onChange={handleTabChange}
          sx={{
            '& .MuiTab-root': { minWidth: 120 },
            '& .Mui-selected': { color: '#4a8a84' },
            '& .MuiTabs-indicator': { backgroundColor: '#4a8a84' }
          }}
        >
          {documentosTipos.map((tipo) => (
            <Tab key={tipo.id} value={tipo.id} label={tipo.label} />
          ))}
        </Tabs>
      </Box>
      
      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 650 }} aria-label="tabela de documentos">
          <TableHead sx={{ bgcolor: '#f5f5f5' }}>
            <TableRow>
              <TableCell>Título</TableCell>
              <TableCell>Paciente</TableCell>
              <TableCell>Data</TableCell>
              <TableCell>Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {documentosFiltrados.length > 0 ? (
              documentosFiltrados
                .sort((a, b) => {
                  // Ordenação por data (mais recente primeiro)
                  const dataA = a.data.split('/').reverse().join('-');
                  const dataB = b.data.split('/').reverse().join('-');
                  return new Date(dataB) - new Date(dataA);
                })
                .map((documento) => (
                  <TableRow
                    key={documento.id}
                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                  >
                    <TableCell component="th" scope="row">
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <FileIcon sx={{ mr: 1, color: '#4a8a84' }} />
                        {documento.titulo}
                      </Box>
                    </TableCell>
                    <TableCell>{getPacienteNome(documento.pacienteId)}</TableCell>
                    <TableCell>{formatarData(documento.data)}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex' }}>
                        <Tooltip title="Baixar">
                          <IconButton 
                            size="small" 
                            onClick={() => handleDownload(documento)}
                            sx={{ color: '#4a8a84' }}
                          >
                            <DownloadIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        
                        <Tooltip title="Imprimir">
                          <IconButton 
                            size="small" 
                            onClick={() => handlePrint(documento)}
                            sx={{ color: '#4a8a84' }}
                          >
                            <PrintIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        
                        <Tooltip title="Compartilhar">
                          <IconButton 
                            size="small" 
                            onClick={() => handleShare(documento)}
                            sx={{ color: '#4a8a84' }}
                          >
                            <ShareIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        
                        <Tooltip title="Editar">
                          <IconButton 
                            size="small" 
                            onClick={() => handleOpenDialog(documento)}
                            sx={{ color: '#4a8a84' }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        
                        <Tooltip title="Excluir">
                          <IconButton 
                            size="small" 
                            onClick={() => handleDeleteDocumento(documento.id)}
                            sx={{ color: '#757575' }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
            ) : (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  Nenhum {
                    tabAtiva === 'relatorios' ? 'relatório' : 
                    tabAtiva === 'declaracoes' ? 'declaração' : 
                    tabAtiva === 'atestados' ? 'atestado' : 
                    'encaminhamento'
                  } encontrado
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      
      {/* Dialog para criar/editar documento */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {currentDocumento ? 'Editar Documento' : `Novo ${getTipoDocumentoSingular(formData.tipo)}`}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Título"
                value={formData.titulo}
                onChange={(e) => handleInputChange('titulo', e.target.value)}
                error={!!formErrors.titulo}
                helperText={formErrors.titulo}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Tipo de Documento</InputLabel>
                <Select
                  value={formData.tipo}
                  label="Tipo de Documento"
                  onChange={(e) => handleInputChange('tipo', e.target.value)}
                >
                  {documentosTipos.map((tipo) => (
                    <MenuItem key={tipo.id} value={tipo.id}>
                      {tipo.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth error={!!formErrors.pacienteId}>
                <InputLabel>Paciente</InputLabel>
                <Select
                  value={formData.pacienteId}
                  label="Paciente"
                  onChange={(e) => handleInputChange('pacienteId', e.target.value)}
                >
                  <MenuItem value="">
                    <em>Selecione um paciente</em>
                  </MenuItem>
                  {pacientes.map((paciente) => (
                    <MenuItem key={paciente.id} value={paciente.id.toString()}>
                      {paciente.nome}
                    </MenuItem>
                  ))}
                </Select>
                {formErrors.pacienteId && (
                  <FormHelperText>{formErrors.pacienteId}</FormHelperText>
                )}
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth error={!!formErrors.data}>
                <InputLabel>Data do Documento</InputLabel>
                <TextField
                  type="date"
                  value={formData.data ? formData.data.toISOString().split('T')[0] : ''}
                  onChange={(e) => handleInputChange('data', new Date(e.target.value))}
                  error={!!formErrors.data}
                  helperText={formErrors.data}
                />
              </FormControl>
            </Grid>
            
            {formData.tipo === 'atestados' && (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Período de Afastamento (dias)"
                    type="number"
                    value={formData.diasAfastamento}
                    onChange={(e) => handleInputChange('diasAfastamento', e.target.value)}
                    InputProps={{ inputProps: { min: 1 } }}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Motivo do Afastamento"
                    value={formData.motivoAfastamento}
                    onChange={(e) => handleInputChange('motivoAfastamento', e.target.value)}
                    placeholder="Ex: quadro de ansiedade e estresse"
                  />
                </Grid>
              </>
            )}
            
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" gutterBottom>
                Conteúdo do Documento
              </Typography>
              {formData.tipo === 'atestados' && (
                <Box sx={{ mb: 2, p: 2, bgcolor: 'rgba(74, 138, 132, 0.05)', borderRadius: 1 }}>
                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    Informações sobre Atestados
                  </Typography>
                  <Typography variant="body2" paragraph>
                    • Os atestados psicológicos são documentos que informam sobre o estado psicológico do paciente e podem justificar afastamentos por questões de saúde mental.
                  </Typography>
                  <Typography variant="body2" paragraph>
                    • É importante especificar o período de afastamento recomendado e, quando apropriado, incluir o CID correspondente.
                  </Typography>
                  <Typography variant="body2">
                    • Conforme Resolução CFP nº 06/2019, os atestados devem se restringir às informações necessárias, preservando o sigilo profissional.
                  </Typography>
                </Box>
              )}
              <TextField
                fullWidth
                multiline
                rows={12}
                value={formData.conteudo}
                onChange={(e) => handleInputChange('conteudo', e.target.value)}
                error={!!formErrors.conteudo}
                helperText={formErrors.conteudo}
                sx={{ mt: 1 }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button 
            onClick={handleSaveDocumento} 
            variant="contained"
            sx={{ bgcolor: '#4a8a84', '&:hover': { bgcolor: '#3a7a74' } }}
          >
            Salvar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Documentos; 